import { useQuery } from "@tanstack/react-query";
import { communityApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type { CommunityResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * A single community (GET /communities/{id}), returned VERBATIM — name,
 * description, member_count, visibility / join_mode meta, and the server's
 * `is_member` flag that drives the Join vs Leave CTA. No derived state.
 *
 * Disabled until an id is present so an unresolved route param never fires a
 * bogus request. Cached under the `social.community(id)` key; a join / leave
 * invalidates that slot so the page reflects the server's refetched membership +
 * count (never an optimistic local +1).
 */
export function useCommunity(communityId: string | undefined) {
  const { user, loading } = useAuth();
  const enabled = Boolean(communityId && user?.uid) && !loading;
  const query = useQuery<CommunityResponse>({
    queryKey: [...queryKeys.social.community(communityId ?? ""), user?.uid ?? null],
    queryFn: () => communityApi.get(communityId as string),
    enabled,
    refetchOnMount: "always",
  });
  return {
    ...query,
    isLoading: enabled && !query.isError && (!query.isFetchedAfterMount || query.isLoading),
    data: enabled && query.isFetchedAfterMount && !query.isError ? query.data : undefined,
  };
}
